import { useEffect, useRef, useState } from 'react';

// The gold cursor is used only with a mouse, on a window at least 1024px wide (the Tailwind
// `lg` breakpoint) and when the visitor has not asked for less motion. src/index.css uses the
// same rule, so the system cursor is hidden only while the gold one is actually shown.
export const CUSTOM_CURSOR_QUERY = '(hover: hover) and (pointer: fine) and (min-width: 1024px) and (prefers-reduced-motion: no-preference)';
const INTERACTIVE_SELECTOR = 'a, button, [role="button"], summary';
// Text fields keep the normal system cursor, so the gold one steps aside over them.
const TEXT_FIELD_SELECTOR = 'input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=reset]):not([type=range]):not([type=color]):not([type=file]):not([type=image]), textarea, select, [contenteditable="true"], [contenteditable=""]';

function cursorMedia() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(CUSTOM_CURSOR_QUERY);
}

export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [enabled, setEnabled] = useState(() => Boolean(cursorMedia()?.matches));
  const [isHovering, setIsHovering] = useState(false);
  const [isOverField, setIsOverField] = useState(false);
  const mousePos = useRef({ x: 0, y: 0 });
  const ringPos = useRef({ x: 0, y: 0 });
  const isHoveringRef = useRef(false);

  // Follow window resizes, pointer changes and the reduced-motion setting while the page is open.
  useEffect(() => {
    const media = cursorMedia();
    if (!media) return undefined;
    const update = () => setEnabled(media.matches);
    update();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener?.(update);
    return () => media.removeListener?.(update);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const { body } = document;
    body.classList.add('custom-cursor-enabled');
    body.classList.remove('custom-cursor-disabled');
    let rafId = 0;

    const onMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX - 4}px, ${e.clientY - 4}px)`;
      }
    };

    const animate = () => {
      const dx = mousePos.current.x - ringPos.current.x;
      const dy = mousePos.current.y - ringPos.current.y;
      ringPos.current.x += dx * 0.15;
      ringPos.current.y += dy * 0.15;
      if (ringRef.current) {
        const size = isHoveringRef.current ? 25 : 18;
        ringRef.current.style.transform = `translate(${ringPos.current.x - size}px, ${ringPos.current.y - size}px)`;
      }
      rafId = requestAnimationFrame(animate);
    };

    const showTarget = (target) => {
      const element = target instanceof Element ? target : null;
      const overField = Boolean(element?.closest(TEXT_FIELD_SELECTOR));
      const hovering = !overField && Boolean(element?.closest(INTERACTIVE_SELECTOR));
      isHoveringRef.current = hovering;
      setIsHovering(hovering);
      setIsOverField(overField);
    };
    // One delegated listener covers links and buttons on every page, including ones that
    // appear after the first load (route changes, menus, dialogs).
    const onPointerOver = (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      showTarget(e.target);
    };
    const onPointerOut = (e) => {
      if (!e.relatedTarget) showTarget(null);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('pointerover', onPointerOver);
    document.addEventListener('pointerout', onPointerOut);
    rafId = requestAnimationFrame(animate);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('pointerover', onPointerOver);
      document.removeEventListener('pointerout', onPointerOut);
      cancelAnimationFrame(rafId);
      body.classList.remove('custom-cursor-enabled');
      isHoveringRef.current = false;
      setIsHovering(false);
      setIsOverField(false);
    };
  }, [enabled]);

  // Touch devices, narrow windows and reduced motion keep the normal system cursor.
  if (!enabled) return null;

  const fieldStyle = isOverField ? { opacity: 0 } : undefined;
  return (
    <>
      <div ref={dotRef} className="cursor-dot hidden lg:block" style={fieldStyle} aria-hidden="true" />
      <div ref={ringRef} className={`cursor-ring hidden lg:block ${isHovering ? 'active' : ''}`} style={fieldStyle} aria-hidden="true" />
    </>
  );
}
